<template>
  <b-overlay :show="loadingFile" class="row h-100 bg-white" v-bind:class="{'toggle': toggle}">
    <rs-select-button
      :tooltip="getSelectionTooltip"
      :disabled="selectButtonDisabled"
      :on-click="selectToDoc"
      :required-file-type="requiredFileType"
      :accepted-file-extensions="acceptedFileExtensions"
    />
    <rs-file-table
      :folder-elements="folderElements"
      :file-elements="fileElements"
      :loading-elements="loadingElements || loading"
      :is-empty-folder="isEmptyFolder"
    />
  </b-overlay>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faFolder, faArrowAltCircleDown } from '@fortawesome/free-solid-svg-icons';

import { ioEDFilesGet } from '@/febos/servicios/api/repositories.api';
import ApprovalStateTypes, { ApprovalStateNames } from '@/febos/global/documentManagement/mixins/ApprovalStateTypes';
import PublicationStateTypes from '@/febos/global/documentManagement/mixins/PublicationStateTypes';
import ValidityStateTypes from '@/febos/global/documentManagement/mixins/ValidityStateTypes';
import RsFileTable from '@/febos/global/customDocuments/components/repository-selector/RSFileTable';
import RsSelectButton from '@/febos/global/customDocuments/components/repository-selector/RSSelectButton';

library.add(faFolder, faArrowAltCircleDown);
export default {
  name: 'rs-navigation-panel',
  components: {
    RsSelectButton,
    RsFileTable,
  },
  props: {
    toggle: Boolean,
    addFile: {
      type: Function
    },
    removeFile: {
      type: Function
    },
    files: {
      type: Array,
      default: () => []
    },
    maxFiles: {
      type: Number,
      required: true
    },
    requiredFileType: String,
    acceptedFileExtensions: String,
    requiredApprovalState: Number,
  },
  data() {
    return {
      loadingFile: false,
      userPermissions: {
        read: 0,
        write: 0,
        totalControl: 0
      },
      folderElements: [],
      fileElements: [],
      selectedElement: {},
      activeElement: null,
      isEdit: false,
      filesToUpload: [],
      dragging: false,
      individualProgress: {},
      progress: 0,
      approvalStateTypes: ApprovalStateTypes,
      publicationStateTypes: PublicationStateTypes,
      validityStateTypes: ValidityStateTypes,
      approvalStateNames: ApprovalStateNames,
    };
  },
  methods: {
    ...mapActions('Modals', [
      'showModals',
      'closeModal'
    ]),
    async selectToDoc() {
      if (this.activeElement.type === 'file') {
        this.loadingFile = true;
        let fileInfo = await ioEDFilesGet({
          repositoryId: 0,
          fileId: this.activeElement.id
        });
        this.loadingFile = false;
        const selectedFile = {
          id: fileInfo.data.archivo.archivoId,
          archivoId: fileInfo.data.archivo.archivoId,
          name: fileInfo.data.archivo.nombre,
          source: 'repositorio',
          origen: 'repositorio',
          tipo: 'repositorio',
          currentDescription: fileInfo.data.archivo.nombre,
          mime: fileInfo.data.archivo.mime,
          date: fileInfo.data.archivo.fechaCreacion,
          path: fileInfo.data.archivo.adjuntoUrl,
          failed: false,
        };
        const exist = this.files.find((file) => file.id === selectedFile.id);
        if (!exist) {
          this.addFile(selectedFile);
        }
        fileInfo = {};
      }
    },
    onChange() {
      this.filesToUpload = [...this.$refs.file.files];
    },
    getFileExtension(file) {
      if (!file) return '';
      const lastDot = file.name.lastIndexOf('.');
      if (lastDot === -1) {
        return '';
      }
      return file.name.substring(lastDot + 1).toUpperCase();
    },
  },
  computed: {
    ...mapGetters('Empresas', [
      'companyId', 'iut', 'company'
    ]),
    ...mapGetters('Usuario', [
      'currentUser',
      'hasPermission'
    ]),
    ...mapGetters('Modals', [
      'modalName'
    ]),
    ...mapGetters('DocManagement', [
      'getSelectedRepositoryId',
      'getSelectedFolderId',
      'getSelectedElement',
      'getTreeElements',
      'getFilesStack',
      'getRepositories',
      'loadingElements',
      'loading',
      'getPermissionsStack'
    ]),
    fileExistInFilesArray() {
      if (!this.files || this.files.length === 0) {
        return false;
      }
      console.log('files', this.files);
      console.log('files', this.files);
      if (!this.activeElement) {
        return false;
      }
      if (this.activeElement.type !== 'file') {
        return false;
      }
      const exist = this.files.find((file) => file.id === this.activeElement.id);
      return !!exist;
    },
    getSelectionTooltip() {
      if (this.files && this.files.length >= this.maxFiles) {
        return 'Has alcanzado el límite de archivos';
      }
      if (this.fileExistInFilesArray) {
        return 'Este archivo ya ha sido seleccionado';
      }
      if (this.userPermissions.read === 0) {
        return 'No tienes permisos para ver este archivo';
      }
      if (!this.activeElement) {
        return 'Selecciona un archivo para continuar';
      }
      if (this.activeElement.type !== 'file') {
        return 'Selecciona un archivo para continuar';
      }
      if (this.requiredFileType && !this.requiredFileType.includes(this.requiredFileType && this.activeElement.codeType)) {
        return `El archivo seleccionado no es del tipo ${this.requiredFileType}`;
      }
      if (this.acceptedFileExtensions && !this.acceptedFileExtensions
        .toUpperCase().includes(this.getFileExtension(this.activeElement))
      ) {
        return 'El archivo seleccionado no tiene una extensión permitida';
      }
      if (this.requiredApprovalState
        && this.activeElement.approvalState !== this.requiredApprovalState) {
        return `El archivo seleccionado no tiene el estado de
        aprobación: ${this.approvalStateNames[this.requiredApprovalState]}`;
      }
      return 'Seleccionar archivo';
    },
    repositoryId() {
      let response = '';
      if (this.getSelectedFolderId) {
        response = this.getTreeElements[this.getSelectedFolderId].repositoryId;
      }
      if (this.getSelectedRepositoryId) {
        response = this.getSelectedRepositoryId;
      }
      return response;
    },
    elementName() {
      let response = '';
      if (this.getSelectedRepositoryId) {
        response = this.getRepositories[this.getSelectedRepositoryId].nombreRepositorio;
      }
      if (this.getSelectedFolderId) {
        response = this.getTreeElements[this.getSelectedFolderId].nombre;
      }
      return response;
    },
    isEmptyFolder() {
      return !this.loading
        && !this.loadingElements
        && !this.folderElements.length
        && !this.fileElements.length;
    },
    selectButtonDisabled() {
      console.log('???', this.requiredFileType);
      return this.fileExistInFilesArray
        || this.userPermissions.read === 0
        || !this.activeElement
        || this.activeElement.type !== 'file'
        || this.files.length >= this.maxFiles
        || (this.requiredFileType
          && !this.requiredFileType.includes(this.activeElement.codeType))
        || (this.acceptedFileExtensions && !this.acceptedFileExtensions
          .toUpperCase().includes(this.getFileExtension(this.activeElement)))
        || (this.requiredApprovalState
          && this.activeElement.approvalState !== this.requiredApprovalState);
    }
  },
  watch: {
    getSelectedElement() {
      const selectedElement = this.getSelectedElement || {};
      this.activeElement = this.getSelectedElement || {};
      this.userPermissions.read = selectedElement.loggedUserPermissions?.lectura;
      this.userPermissions.write = selectedElement.loggedUserPermissions?.escritura;
      this.userPermissions.totalControl = selectedElement.loggedUserPermissions?.controlTotal;
    },
    getTreeElements() {
      this.folderElements = [];
      let folders = {};
      if (this.getSelectedRepositoryId) {
        folders = Object.values(this.getTreeElements)
          .filter((element) => (
            element.parentFolder === null && element.repositoryId === this.getSelectedRepositoryId
          ));
      }
      if (this.getSelectedFolderId) {
        folders = Object.values(this.getTreeElements)
          .filter((element) => (
            element.parentFolder === this.getSelectedFolderId
          ));
      }
      this.folderElements = folders.map((folder) => ({
        id: folder.carpetaId,
        name: folder.nombre,
        fullpathId: folder.fullpathId,
        parentFolder: folder.parentFolder,
        repositoryId: folder.repositoryId,
        creationDate: folder.fechaCreacion,
        ownerId: folder.propietario.id,
        owner: folder.propietario.alias,
        numberOfFiles: folder.cantidadArchivos,
        numberOfFolders: folder.cantidadCarpetas,
        tags: [],
        keywords: [],
        type: 'folder'
      }));
    },
    getFilesStack() {
      this.fileElements = [];
      let files = [];
      if (this.getSelectedRepositoryId) {
        files = Object.values(this.getFilesStack)
          .filter((element) => (
            element.parentFolder === null && element.repositoryId === this.getSelectedRepositoryId
          ));
      }
      if (this.getSelectedFolderId) {
        files = Object.values(this.getFilesStack)
          .filter((element) => (
            element.parentFolder === this.getSelectedFolderId
          ));
      }
      this.fileElements = files.map((file) => ({
        id: file.archivoId,
        name: file.nombre,
        codeType: file.codType,
        creationDate: file.fechaCreacion,
        publicationDate: file.fechaPublicacion,
        parentFolder: file.parentFolder,
        ownerId: file.propietario.id,
        owner: file.propietario.alias,
        size: file.pesoArchivo,
        mimeFile: file.mime,
        type: 'file',
        documentId: file.documentoId,
        repositoryId: file.repositoryId,
        urlFile: file.urlAdjunto,
        downloadURL: `${file.urlDescarga}?t=${this.currentUser.token}&e=${this.companyId}`,
        publicLink: file.esPublico === 'S' ? file.urlDescarga : undefined,
        isPublic: file.esPublico === 'S',
        externalVersion: file.versionExterna,
        publicationState: file.estadoPublicacion,
        validityState: file.estadoVigencia,
        approvalState: file.estadoAprobacion,
        expirationDate: file.fechaVencimiento,
        expirationType: file.tipoExpiracion
      }));
    },
    getSelectedFolderId() {
      this.folderElements = [];
      this.fileElements = [];
      if (this.getSelectedFolderId) {
        this.selectedElement = this.getTreeElements[this.getSelectedFolderId];
      }
    },
    getSelectedRepositoryId() {
      this.folderElements = [];
      this.fileElements = [];
      if (this.getSelectedRepositoryId) {
        this.selectedElement = this.getRepositories[this.getSelectedRepositoryId];
      }
    },
    progress(value) {
      if (value === 100) {
        this.filesToUpload = [];
        this.dragging = false;
        this.individualProgress = {};
        this.progress = 0;
      }
    },
    selectedElement() {
      if (this.selectedElement.propietarioId === this.companyId) {
        const activo = 1;
        this.userPermissions.read = activo;
        this.userPermissions.write = activo;
      }
    }
  }
};
</script>
